import { useCallback, useContext, useEffect, useState } from 'react';

import {
  DragDropContext,
  Droppable,
  Draggable,
  DropResult,
} from 'react-beautiful-dnd';

import { AssetContext } from '@pages/Asset/Asset.query';
import FieldConfiguration, {
  IFieldConfiguration,
} from '@models/FieldConfiguration.model';

import { useMutation, useQueryClient } from 'react-query';
import { updateOrderFieldConfiguration } from '@services/queries/fieldsConfiguration';
import { isArrayOfObjectsEqual } from '@utils';
import { Container, Done, Buttons, Cancel } from './PinnedData.styles';
import { PinnedField } from './components';

export const PinnedData = () => {
  const { configQuery, reOrderPinnedData, unpinPinnedData, restorePinnedData } =
    useContext(AssetContext);

  const queryClient = useQueryClient();

  const [overflow, setOverflow] = useState<boolean>(false);
  const [configLocal, setConfigLocal] = useState<IFieldConfiguration[] | null>(
    null,
  );

  const config = configQuery?.data;

  useEffect(() => {
    if (
      config &&
      !reOrderPinnedData.get() &&
      !unpinPinnedData.get() &&
      !restorePinnedData.get()
    ) {
      setConfigLocal(config);
    }
  }, [config, configLocal]);

  useEffect(() => {
    if (restorePinnedData.get()) {
      setConfigLocal(FieldConfiguration.defaultArray('productHeader'));
      reOrderPinnedData.set(true);
    }
  }, [restorePinnedData.get()]);

  const onDragEnd = useCallback(
    (result: DropResult) => {
      const draggedItem = configLocal?.find(
        item => item.id === result.draggableId,
      );

      if (configLocal && result.destination && draggedItem) {
        const configNoDragged = configLocal
          .sort((a, b) => a.order - b.order)
          .filter(item => item.id !== result.draggableId);

        configNoDragged.splice(result.destination.index, 0, draggedItem);

        const newConfigOrdered = configNoDragged.map((item, idx) => ({
          ...item,
          order: idx,
        }));

        setConfigLocal(newConfigOrdered);
      }
    },
    [configLocal],
  );

  const removeItem = useCallback(
    (id: string) => {
      if (configLocal) {
        const configNoDragged = configLocal
          .sort((a, b) => a.order - b.order)
          .filter(item => item.id !== id);

        const newConfigOrdered = configNoDragged.map((item, idx) => ({
          ...item,
          order: idx,
        }));

        setConfigLocal(newConfigOrdered);
      }
    },
    [configLocal],
  );

  const cancel = useCallback(() => {
    if (config) {
      setConfigLocal(config);
      reOrderPinnedData.set(false);
      unpinPinnedData.set(false);
      restorePinnedData.set(false);
    }
  }, [config]);

  const mutationUpdateOrderFieldConfiguration = useMutation(
    updateOrderFieldConfiguration,
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['fieldsConfiguration', 'productHeader']);
        reOrderPinnedData.set(false);
        unpinPinnedData.set(false);
        restorePinnedData.set(false);
      },
    },
  );

  if (
    !configQuery ||
    configQuery.isLoading ||
    configQuery.isError ||
    !config ||
    !configLocal
  ) {
    return <div />;
  }

  return (
    <div>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="droppable" direction="vertical">
          {provided => (
            <Container
              {...provided.droppableProps}
              ref={provided.innerRef}
              height={configLocal.length * 34}
              data-testid="pinned-data"
              overflowAuto={overflow}
            >
              {configLocal.map((item, index) => (
                <Draggable
                  draggableId={item.id}
                  key={item.id}
                  index={index}
                  {...provided.droppableProps}
                  isDragDisabled={!reOrderPinnedData.get()}
                >
                  {provided2 => (
                    <div
                      ref={provided2.innerRef}
                      {...provided2.draggableProps}
                      {...provided2.dragHandleProps}
                    >
                      <PinnedField
                        item={item}
                        index={index}
                        removeItem={() => {
                          removeItem(item.id);
                        }}
                        setOverflow={setOverflow}
                      />
                    </div>
                  )}
                </Draggable>
              ))}
            </Container>
          )}
        </Droppable>
      </DragDropContext>

      {(reOrderPinnedData.get() ||
        unpinPinnedData.get() ||
        restorePinnedData.get()) && (
        <Buttons>
          <Cancel onClick={cancel} type="button">
            Cancel
          </Cancel>
          <Done
            type="button"
            disabled={
              mutationUpdateOrderFieldConfiguration.isLoading ||
              isArrayOfObjectsEqual(
                config.map(({ fieldName, order }) => ({ fieldName, order })),
                configLocal.map(({ fieldName, order }) => ({
                  fieldName,
                  order,
                })),
              )
            }
            onClick={() => {
              const updatedOrders = configLocal.map(
                (fieldConfiguration, index) => {
                  if (fieldConfiguration.customField) {
                    return {
                      order: index,
                      customFieldId: fieldConfiguration.customField.id,
                    };
                  }
                  return {
                    order: index,
                    fieldName: fieldConfiguration.fieldName,
                  };
                },
              );

              mutationUpdateOrderFieldConfiguration.mutate({
                location: 'productHeader',
                data: { updatedOrders },
              });
            }}
          >
            Done
          </Done>
        </Buttons>
      )}
    </div>
  );
};
